import { useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { CurrentUser } from "./roles";

/**
 * Loads the authenticated user's identity + DM role flags. The API endpoint is
 * idempotent / cheap (one DM_USER_ROLE lookup per request); the hook keeps the
 * result in React Query so every component shares one cached payload.
 *
 * When the SPA gains a real MSAL provider, the axios interceptor will attach
 * the bearer token automatically — no change required here.
 *
 * In dev mode against the local API (which falls back to NoOpUserContext when
 * no AAD app is configured), this returns the permissive dev identity. See
 * audit/15-permission-model.md §1 + Q1.
 */
export const useCurrentUser = () =>
  useQuery({
    queryKey: ["currentUser"],
    queryFn: async (): Promise<CurrentUser> => {
      const response = await apiClient.get<CurrentUser>(APIS.CurrentUser);
      return response.data;
    },
    // Identity rarely changes within a session; cache aggressively.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

/**
 * Returns just the user's DM role names (or undefined while loading).
 * Convenience wrapper for components that only care about role membership.
 */
export const useUserRoles = (): string[] | undefined => {
  const { data } = useCurrentUser();
  return data?.roles;
};

/**
 * True when the authenticated user holds AT LEAST ONE of the requested roles.
 * Returns false while loading (fail-closed) so guards don't flicker.
 */
export const useHasAnyRole = (roles: string[]): boolean => {
  const userRoles = useUserRoles();
  if (!userRoles || roles.length === 0) return false;
  const set = new Set(userRoles);
  return roles.some((r) => set.has(r));
};
